import { Reducer } from 'redux';
import { ActionType, getType } from 'typesafe-actions';
import { cardReducer } from '../Card/CardReducer';
import { CardActions } from '../Card/CardActions';
import { LevelActions } from './LevelActions';
import LevelStateHelper from './LevelStateHelper';
import { FTUXStateHelper } from '../Game/FTUX/FTUXStateHelper';
import { LevelTask } from './LevelTask';
import { RewardType } from 'Utility/Rewards/RewardType';
import { checkHelperFriendVisible } from './HelperFriend/HelperFriendStateHelper';
import { resetEventHandlerData, track, TrackMethod } from 'Utility/Tracking/Track';
import { MissionTrackingEventName } from 'Utility/Tracking/TrackingEvent';
import { LevelStatus } from './LevelStatus';
import { IGameState } from '../Game/IGameState';
import { ProgressStateHelper } from '../Player/Progress/ProgressStateHelper';
import { InventoryStateHelper } from '../Player/Inventory/InventoryStateHelper';
import { trackLevelStart } from 'Utility/Tracking/ActivityTracking';
import CardStateHelper from 'State/Card/CardStateHelper';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import UIStateHelper from 'State/UI/UIStateHelper';
import { PopupID } from 'State/UI/PopupID';
import { LevelFailReason } from 'State/Game/GameActions';
import { CardType } from 'Prototype/Level/Card/CardType';
import { report } from 'report';
import { AvoTracking } from 'Utility/Tracking/Avo/AvoTracking';
import { trackResourceEvent } from 'Utility/Tracking/TrackingHelpers/Transaction';
import { TActionType, TransactionType } from 'Utility/Tracking/DataSet/Transaction';

const tasksToClear: LevelTask[] = [LevelTask.FTUX_GOLD];
export const LevelReducer: Reducer<IGameState> = (
    state: IGameState,
    action: ActionType<typeof LevelActions | typeof CardActions>
): IGameState => {
    state = {
        ...state,
        levelState: {
            ...state.levelState,
            tasks: state.levelState.tasks.filter(task => !tasksToClear.includes(task.task)),
        },
    };

    switch (action.type) {
        case getType(LevelActions.setLevelLoaded):
            try {
                state = {
                    ...state,
                    levelState: {
                        ...state.levelState,
                        status: LevelStatus.LOADED,
                        ...action.payload,
                    },
                };

                state = CardStateHelper.onLevelLoaded(state);

                state = LevelStateHelper.getCoinRewardAmount(state);
                state = LevelStateHelper.ensureBoosterUses(state);
                state = LevelStateHelper.getBoosterPrices(state);
                state = LevelStateHelper.useExtraCardBoosters(state);

                // FTUX tasks and level unlocks.
                state = FTUXStateHelper.setLevelTasks(state);
                state = FTUXStateHelper.setLevelComponents(state);

                track('mLevelMoves', state.levelState.cardsState.inStack.length);
            } catch (error) {
                report('Failed to set level loaded.', {
                    once: true,
                    sampleRate: 0.1,
                    fingerprint: 'LevelReducer:setLevelLoaded:FailedToSetLevelLoaded',
                    tags: {
                        levelNumber: state.levelState.levelNumber,
                    },
                    state,
                });
            }

            break;
        case getType(LevelActions.levelIntro):
            if (state.levelState.status === LevelStatus.LOADED) {
                state = {
                    ...state,
                    levelState: {
                        ...state.levelState,
                        status: LevelStatus.INTRO,
                    },
                };
                track('mPlus5Used', 0, TrackMethod.SET);
                track('mMassRemoveUsed', 0, TrackMethod.SET);
                track('mRemoveOneUsed', 0, TrackMethod.SET);
                track('mChangerUsed', 0, TrackMethod.SET);
                track('mJokerUsed', 0, TrackMethod.SET);
                track('mUndoUsed', 0, TrackMethod.SET);
                track('mCoinsSpent', 0, TrackMethod.SET);
                track('mExtraCardsBought', 0, TrackMethod.SET);
                track('mExtraCardsWon', 0, TrackMethod.SET);
                track('mJokerCardsWon', 0, TrackMethod.SET);
                track('mStreakNum', 0, TrackMethod.SET);
                track('mStreakColorNum', 0, TrackMethod.SET);
                track('mHighestPlayStreak', 0, TrackMethod.SET);
                track('mNumBoostersUsedLossAversion', 0, TrackMethod.SET);
                track('mMovesMade', 0, TrackMethod.SET);
                track('mLossAversionCoinsSpent', 0, TrackMethod.SET);
                track('mCoinsBoughtWithAds', 0, TrackMethod.SET);

                const level = ProgressStateHelper.getLevelData(
                    state,
                    state.levelState.levelNumber,
                    state.levelState.levelType
                );
                const hasWonLevel = Boolean(level?.stars);
                track('mIsSuccessRetry', Number(hasWonLevel), TrackMethod.SET);

                const starsToGain = Math.max(3 - (level?.stars ?? 0), 0);
                track('mChestStarsToGain', starsToGain, TrackMethod.SET);

                //Check which FTUX needs to be executed
                state = LevelStateHelper.queueFTUXTasksIfNeeded(state);
            }
            break;
        case getType(LevelActions.levelActive):
            if (state.levelState.status === LevelStatus.INTRO) {
                // Add level to list to account for an attempt at the level
                const levels = state.playerState.levels[state.levelState.levelType].slice();
                const currentLevel = ProgressStateHelper.getLevelData(
                    state,
                    state.levelState.levelNumber,
                    state.levelState.levelType
                );

                if (currentLevel === undefined) {
                    levels.push({
                        levelNumber: state.levelState.levelNumber,
                        stars: undefined,
                    });
                }

                // Charge for level once level becomes active
                if (state.levelState.isFreePlay) {
                    track('mFreeEntryUsed', 1, TrackMethod.ADD);

                    state = {
                        ...state,
                        playerState: InventoryStateHelper.setBoosterFreePlay(
                            state.playerState,
                            state.playerState.inventory[RewardType.FREE_PLAY] - 1,
                            { queueTask: false }
                        ),
                    };
                }
                state = {
                    ...state,
                    playerState: InventoryStateHelper.setCoins(
                        state.playerState,
                        state.playerState.inventory.coins - state.levelState.levelCost
                    ),
                };

                trackResourceEvent(state, { tActionType: TActionType.LEVEL_ENTRY, transactionType: TransactionType.INVENTORY });

                state = {
                    ...state,
                    levelState: {
                        ...state.levelState,
                        status: LevelStatus.ACTIVE,

                        // Reset undo history/count at the start of each level.
                        cardsState: {
                            ...state.levelState.cardsState,
                            undoHistory: [],
                        },
                    },
                    playerState: {
                        ...state.playerState,
                        progress: {
                            ...state.playerState.progress,
                        },
                        levels: {
                            ...state.playerState.levels,
                            [state.levelState.levelType]: levels,
                        },
                    },
                };

                state = CardStateHelper.onLevelStarted(state);

                track('mIsPlinkoLevel', Number(CardStateHelper.hasCardOfType(state, CardType.plinko)), TrackMethod.SET);

                resetEventHandlerData(MissionTrackingEventName.missionStarted, state);

                //
                state = trackLevelStart(state);
            }
            break;
        case getType(LevelActions.setLevelLeaderboard):
            state = {
                ...state,
                levelState: {
                    ...state.levelState,
                    leaderboard: action.payload,
                },
            };
            break;
        case getType(LevelActions.showGoldCardTutorial):
            state = LevelStateHelper.setStateTaskData(state, {
                task: LevelTask.FTUX_GOLD,
            });
            break;
        case getType(LevelActions.exitLevel):
            const levelNumber = state.levelState.levelNumber;
            const isPlinkoLevel = WorldDataHelper.isPlinkoLevel(state.levelState.levelNumber);
            state = {
                ...state,
                UIState: UIStateHelper.showPopup(
                    state.UIState,
                    isPlinkoLevel ? PopupID.CONFIRM_QUIT_LEVEL : PopupID.LEVEL_POPUP,
                    isPlinkoLevel
                        ? { levelNumber, endLevelClicked: true }
                        : {
                              levelNumber,
                              selectedPreBoosters: state.levelState.preBoosters.filter(
                                  booster => state.playerState.inventory[booster] > 0
                              ),
                              failReason: LevelFailReason.NO_MORE_MATCHES,
                          }
                ),
            };
            break;
    }

    if (state.levelState.status === LevelStatus.ACTIVE) {
        const previousState = { ...state };

        // Handle active level state actions.
        state = cardReducer(state, action);

        state = checkHelperFriendVisible(state);

        // Update clear cards, jokers and streaks quests.
        state = LevelStateHelper.updateQuestProgress(state, previousState);

        // TODO: combine this with the code that applies the booster
        /* state = {
            ...state,
            playerState: {
                ...state.playerState,
                inventory: {
                    ...state.playerState.inventory,
                    [BoosterType.PLUS_ONE]: 0,
                },
            },
        }; */

        // Update the booster prices
        state = LevelStateHelper.getBoosterPrices(state);
        state = LevelStateHelper.handleLevelComplete(state);
    }

    return state;
};
